import { useEffect, useState } from "react";
import { PrimaryMaterial } from "../../../models/PrimaryMaterial";
import api from "../../../services/ApiService";
import { ApiResponse } from "../../../models/ApiResponse";
import { SettingGrid } from "../../../components/ManageSettingComponents/SettingGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import moment from "moment";
import { Button, Switch } from "@mui/material";
import { toast } from "react-toastify";
import PrimaryMaterialModal from "./PrimaryMaterialModal";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SessionDetails } from "../../../models/SessionDetails";
import { DATE_TIME_FORMAT } from "../../../utils/constants";
import { GRID_ACTION_DELETE_ICON, GRID_ACTION_EDIT_ICON } from "../../../components/GridUtilityComponents";
import SessionService from "../../../services/SessionService";
import { Permissions } from "../../../models/Enum";

export default function PrimaryMaterialSetting() {
  const [primaryMaterialList, setPrimaryMaterialList] = useState<
    PrimaryMaterial[]
  >([]);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);

  const PrimaryMaterialGridCollumns: GridColDef[] = [
    { field: "id", headerName: "Id", width: 100 },
    { field: "name", headerName: "Primary Material", width: 300 },
    { field: "leagoCode", headerName: "LEAGO Code", width: 160 },
    { field: "pimKey", headerName: "PIM Key", width: 200},
    {
      field: "isActive",
      headerName: "Is Active",
      width: 200,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <Switch
            checked={params.value}
            onChange={async (e) => {
              await updatePrimaryMaterialActive(params.row, e.target.checked);
            }}
            color={"primary"}
            inputProps={{ "aria-label": "controlled" }}
            disabled = {!manageAppSetting}
          />
        </>
      ),
    },
    // {
    //   field: "updatedDate",
    //   headerName: "Updated Date",
    //   width: 200,
    //   valueFormatter: (params) => moment(params?.value).format(DATE_TIME_FORMAT),
    // },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 200,
      getActions: (params: any) => [
        GRID_ACTION_EDIT_ICON(params, handleEditClickOpen),
        GRID_ACTION_DELETE_ICON(params, handleDeleteModalOpen)
      ],
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const getPrimaryMaterialRepsonse = await api.get<
      ApiResponse<PrimaryMaterial[]>
    >("/Picklist/primary-materials");
    setPrimaryMaterialList(getPrimaryMaterialRepsonse?.data || []);
  };

  const handleEditClickOpen = (gridParams: any) => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
    formik.setValues({ ...gridParams.row });
  };

  const handleAddClickOpen = () => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
  };

  const handleDeleteModalOpen = (gridParams: any) => {
    setDeleteModalOpen(true);
    formik.setValues({ ...gridParams.row });
  };

  const handleCloseModal = () => {
    setShowAddUpdateModal(false);
    setDeleteModalOpen(false);
    formik.resetForm();
  };

  const updatePrimaryMaterialActive = (
    row: PrimaryMaterial,
    isActive: boolean
  ) => {
    row.isActive = isActive;
    try {
      api
        .put<ApiResponse<PrimaryMaterial>>("/Picklist/primary-material", row)
        .then(async (response: any) => {
          if (response.isSuccess) {
            toast.success(response.message);
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const deleteSubmit = () => {
    try {
      api
        .delete<ApiResponse<SessionDetails>>(
          `/Picklist/primary-material/${formik.values.id}`
        )
        .then(async (response: any) => {
          if (response.isSuccess) {
            setDeleteModalOpen(false);
            toast.success(response.message);
            formik.resetForm();
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: 0,
      name: "",
      leagoCode: "",
      isActive: true,
      pimKey: ""
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Primary Material is required")
        .min(1, "Primary Material must be at least 1 characters")
        .max(40, "Primary Material must be at most 40 characters"),
      leagoCode: Yup.string()
        .trim()
        .required("LEAGO Code is required")
        .test("check-leagoCode-duplicate", "LEAGO Code already exists", (value) => {
          if (!value?.trim()) {
            return true;
          }
          return !primaryMaterialList.some(
            (item) =>
              item.leagoCode?.trim().toLowerCase() === value.trim().toLowerCase() &&
              item.id !== formik.values.id
          );
        }),
      pimKey: Yup.string()
        .nullable()
        .max(20, "PIM Key must be at most 20 characters")
        .test(
          "check-pimKey-duplicate",
          "PIM Key already exists",
          (value) => {
            if(!value?.trim()) {
              return true;
            }
            return !primaryMaterialList.some(
              (item) => !!value?.trim() && item.pimKey?.trim().toLowerCase() === value?.trim().toLowerCase() &&
                item.id !== formik.values.id
            );
          }
        ),
    }),
    onSubmit: (values) => {
      setDisableSubmit(true);
      try {
        if (values.id > 0) {
          api
            .put<ApiResponse<number>>("/Picklist/primary-material", {
              id: values.id,
              name: values.name.trim(),
              leagoCode: values.leagoCode?.trim(),
              isActive: JSON.parse(`${values.isActive}`),
              pimKey: values.pimKey?.trim() || null
            })
            .then(async (response: any) => {
              if (response.isSuccess) {
                setShowAddUpdateModal(false);
                formik.resetForm();
                toast.success(response.message);
                await fetchData();
              } else {
                throw new Error(response.message);
              }
            })
            .catch((error: any) => {
              console.error("Exception from primary material", error);
            });
        } else {
          api
            .post<ApiResponse<number>>("/Picklist/primary-material", {
              name: values.name,
              leagoCode: values.leagoCode?.trim(),
              isActive: JSON.parse(`${values.isActive}`),
              pimKey: values.pimKey?.trim() || null
            })
            .then(async (response: any) => {
              if (response.isSuccess) {
                setShowAddUpdateModal(false);
                formik.resetForm();
                toast.success(response.message);
                await fetchData();
              } else {
                throw new Error(response.message);
              }
            })
            .catch(() => {});
        }
      } catch (err: any) {
        toast.error(err.message);
      }
    },
  });

  return (
    <>
      <div>
        {manageAppSetting && <Button variant="contained" onClick={handleAddClickOpen}>
          Add Primary Material
        </Button>}
        <PrimaryMaterialModal
          isOpen={showAddUpdateModal}
          onClose={handleCloseModal}
          formik={formik}
          isButtonDisabled={disableSubmit}
        />
        <DeleteValidationModal
          addModalOpen={deleteModalOpen}
          handleCloseModal={handleCloseModal}
          deleteSubmit={deleteSubmit}
          fieldIdentifier={"Primary Material"}
        />
      </div>
      <SettingGrid
        data={primaryMaterialList}
        column={PrimaryMaterialGridCollumns}
        sortByField="id"
        sortByOrder="desc"
        columnVisibility={{
          actions: manageAppSetting,
        }}
      />
    </>
  );
}
